import { z } from 'zod';
import { ALERT_SEVERITIES, ALERT_STATUSES, PAYMENT_STATUSES } from '../catalogs/operations.js';
import { COMPLIANCE_ALERT_TYPES } from '../compliance/aml.js';
import { AGING_BUCKETS } from '../credit/delinquency.js';
import { DOCUMENT_CATEGORIES } from './document.js';
import { dateRangeQuerySchema, idSchema, isoDateSchema, listQuerySchema } from './common.js';
import { CASH_SESSION_STATUSES, PAYMENT_METHODS } from './operations.js';
/**
 * Filtros de los listados operativos (cobranza, originación, pagos, caja,
 * cumplimiento y expediente).
 * Todos extienden el contrato `?page=&pageSize=&q=&sortBy=&sortDir=`.
 */
const AGING_BUCKET_IDS = AGING_BUCKETS.map((bucket) => bucket.id);
/** Cola de mora: tramo de atraso, gestor, asesor y agencia. */
export const collectionQueueQuerySchema = listQuerySchema.extend({
    bucket: z.enum(AGING_BUCKET_IDS).optional(),
    collectorId: idSchema.optional(),
    advisorId: idSchema.optional(),
    branchId: idSchema.optional(),
    /** `true` = solo préstamos sin gestor asignado. */
    unassigned: z.enum(['true', 'false']).optional(),
});
/** Vistas de la bandeja de promesas de pago. */
export const PROMISE_VIEWS = ['due', 'upcoming', 'broken', 'kept'];
/** Etiquetas en español de cada vista. */
export const PROMISE_VIEW_LABELS = {
    due: 'Vencen hoy o antes',
    upcoming: 'Próximas',
    broken: 'Incumplidas',
    kept: 'Cumplidas',
};
/** Bandeja de promesas de pago. */
export const promiseListQuerySchema = listQuerySchema.extend({
    view: z.enum(PROMISE_VIEWS).default('due'),
    collectorId: idSchema.optional(),
    branchId: idSchema.optional(),
});
/** Listado de alertas de cumplimiento. */
export const complianceAlertListQuerySchema = listQuerySchema
    .extend({
    status: z.enum(ALERT_STATUSES).optional(),
    type: z.enum(COMPLIANCE_ALERT_TYPES).optional(),
    severity: z.enum(ALERT_SEVERITIES).optional(),
    clientId: idSchema.optional(),
    branchId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'La fecha inicial no puede ser posterior a la final',
    path: ['from'],
});
/**
 * Tablero de originación. `branchId` filtra una agencia: quien ve todas las
 * agencias elige cualquiera; el personal de agencia solo la suya (otra = 403).
 */
export const pipelineQuerySchema = z.object({
    branchId: idSchema.optional(),
});
/** Listado de pagos y su exportación a CSV (mismos filtros en ambos). */
export const paymentListQuerySchema = listQuerySchema.merge(dateRangeQuerySchema).extend({
    loanId: idSchema.optional(),
    clientId: idSchema.optional(),
    receivedBy: idSchema.optional(),
    method: z.enum(PAYMENT_METHODS).optional(),
    status: z.enum(PAYMENT_STATUSES).optional(),
});
/** Cajas de la agencia (vista del supervisor): estado y agencia. */
export const cashSessionListQuerySchema = listQuerySchema.extend({
    status: z.enum(CASH_SESSION_STATUSES).optional(),
    branchId: idSchema.optional(),
});
/** Cajas abiertas en este momento, opcionalmente de una sola agencia. */
export const openCashSessionsQuerySchema = z.object({
    branchId: idSchema.optional(),
});
/**
 * Caja abierta de un cajero. Sin `employeeId` es la del propio usuario;
 * con él, un supervisor consulta la de otro cajero de su agencia.
 */
export const currentCashQuerySchema = z.object({
    employeeId: idSchema.optional(),
});
/**
 * Documentos de un cliente o de un préstamo (uno de los dos es obligatorio),
 * paginados con el contrato estándar de listados.
 */
export const documentListQuerySchema = listQuerySchema
    .extend({
    clientId: idSchema.optional(),
    loanId: idSchema.optional(),
    category: z.enum(DOCUMENT_CATEGORIES).optional(),
})
    .refine((q) => q.clientId !== undefined || q.loanId !== undefined, {
    message: 'Indica el cliente o el préstamo',
    path: ['clientId'],
});
/** Campos del formulario multipart de carga de documentos (además del archivo). */
export const DocumentUploadFieldsSchema = z.object({
    clientId: idSchema,
    loanId: z
        .string()
        .optional()
        .transform((value) => (value === '' ? undefined : value))
        .pipe(idSchema.optional()),
    category: z.enum(DOCUMENT_CATEGORIES, { errorMap: () => ({ message: 'Categoría de documento inválida' }) }),
});
//# sourceMappingURL=queries.js.map